/**
 * Markdown parser — converts raw markdown text to ParsedNode[].
 *
 * Pure function with no Office JS dependency. Fully testable in Node.
 *
 * Supported markdown patterns:
 *   # … ######       Headings (CommonMark levels 1–6 → Word
 *                    Heading1 … Heading6 styles). Earlier we
 *                    matched only ## / ### which caused
 *                    agent-emitted ``####`` lines (level-4
 *                    headings) to fall through and render as
 *                    plain paragraphs in the doc.
 *   **bold**         Bold inline
 *   *italic*         Italic inline
 *   ~sub~ / ^sup^    Subscript / superscript (Pandoc style — H~2~O, m^2^)
 *   [text](url)      Links
 *   > blockquote     Blockquotes (with nesting via >>)
 *   - item / * item  Unordered lists
 *   1. item          Ordered lists
 *   | col | col |    Tables (GFM pipe syntax)
 *   ![alt](url)      Images
 *   [NOT FOUND ...]  Marker flags (not_found, reviewer, fill)
 *
 * SOLID:
 *   SRP — only parses markdown to ParsedNode[]. No rendering.
 *   OCP — adding a new pattern = add a regex + detection branch.
 *         Existing patterns unchanged.
 */

import { filterContentLines } from "./sectionHeaderLogic";
import type { InlineSegment, InlineSegmentType, NotFoundCategory, ParsedNode } from "./types";

// ---------------------------------------------------------------------------
// Regex patterns — ordered by specificity (most specific first)
// ---------------------------------------------------------------------------

// CommonMark allows 1–6 leading hashes for headings. Capping at 6
// matches the spec; Word's built-in styles also cover Heading1–6.
const RE_HEADING = /^(#{1,6})\s+(.+)$/;
const RE_TABLE_ROW = /^\|(.+?)\|?\s*$/;
const RE_TABLE_SEP = /^\|[\s\-:|]+\|?\s*$/;
const RE_IMAGE = /^!\[([^\]]*)\]\(([^)]+)\)$/;
// `[IMAGE DATA: <caption>]` on its own line — an agent pointer to a figure that
// belongs here. The caption (group 1) is fetched by the server's /api/find-image
// route (find_image, fuzzy-matched on the caption) and embedded as a real image
// with the caption beneath it, reusing the markdown-image node path. SSOT for the
// marker is src/csr_sim/flags.yaml.
// Tolerant of the variants the drafting model emits — `[IMAGE DATA: …]`
// (canonical), `[Image: …]`, and a hyphen/en-dash separator — even though the
// server sanitiser normalises these to the canonical form. Belt and suspenders
// so an un-sanitised (e.g. live-streamed) draft still embeds.
const RE_IMAGE_DATA = /^\[\s*image(?:\s+data)?\s*[:–-]\s*(.+?)\s*\]$/i;
const RE_BLOCKQUOTE = /^(>{1,3})\s?(.*)$/;
const RE_UNORDERED_LIST = /^(\s*)[*-]\s+(.+)$/;
const RE_ORDERED_LIST = /^(\s*)\d+\.\s+(.+)$/;

// Inline patterns
const RE_BOLD = /\*\*(.+?)\*\*/g;
const RE_ITALIC = /(?<!\*)\*([^*]+?)\*(?!\*)/g;
const RE_LINK = /\[([^\]]+)\]\(([^)]+)\)/g;
// Pandoc-style subscript/superscript markers — `H~2~O`, `×10^9^/L`, `C~max~`.
// Grammar SSOT is SUB_MARKER_RE / SUP_MARKER_RE in src/csr_sim/draft_sanitizer.py;
// duplicated verbatim here (TS can't import Python) and must stay in lockstep.
// Strict by design so prose tildes/carets never match: non-empty, no
// whitespace, no `%`, ≤15 chars, closing marker required; the `(?<!~)`/`(?!~)`
// lookarounds keep GFM `~~strikethrough~~` intact.
const SUB_SUP_CONTENT = "[A-Za-z0-9+\\-−°/().·]{1,15}";
// Exported: also consumed by services/remarkSubSup.ts (the spine's
// ReactMarkdown plugin) so both split on the identical grammar. Safe to
// share despite the `g` flag — all consumers use matchAll, which clones.
export const RE_SUB_SUP = new RegExp(
  `(?<!~)~(${SUB_SUP_CONTENT})~(?!~)|(?<!\\^)\\^(${SUB_SUP_CONTENT})\\^(?!\\^)`,
  "g",
);
const RE_NOT_FOUND = /\[NOT FOUND\s*-\s*(NOT IN SOURCE|RETRIEVAL FAILURE|AWAITING DOCUMENT)\]/g;
// agent_drafter emits [MISSING INFO: ...] / [MISSING INFO – ...] / [MISSING INFO - ...]
// tags wherever a required data point could not be retrieved. Always rendered as
// a soft pink-red highlight (see wordRenderer). The separator may be a colon,
// an em-dash, or a hyphen — the agent's standard form uses the colon (see
// src/csr_sim/flags.yaml).
const RE_MISSING_INFO = /\[\s*MISSING\s+INFO\s*[:–-]\s*([^\]]+)\]/gi;
// `[CANNOT RESOLVE: …]` is the agent's other acknowledged-gap marker —
// emitted by agent_v3 when retrieval cascade fails and the item is written
// off (see _build_drafting_prompt in src/csr_sim/agent_v3.py). Rendered with
// the same Pink + deep-red treatment as MISSING INFO so writers see one
// consistent visual signal for "this is a gap, fill it".
const RE_CANNOT_RESOLVE = /\[\s*CANNOT\s+RESOLVE\s*[:–-]\s*([^\]]+)\]/gi;
// `[MISSING TABLE: …]` flags a required summary/derived table absent from the
// source library. Same Pink + deep-red treatment as MISSING INFO (see
// wordRenderer) so a missing table is an unmissable gap signal.
const RE_MISSING_TABLE = /\[\s*MISSING\s+TABLE\s*[:–-]\s*([^\]]+)\]/gi;
const RE_REVIEWER = /\[REVIEWER:\s*(.+?)\]/g;
const RE_FILL = /\[FILL:\s*(.+?)\]/g;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * True when ``text`` contains content that a plain ``insertText``
 * cannot render faithfully — it must go through the formatter
 * pipeline (parseMarkdown → renderNodes) to become styled Word
 * content instead of literal markdown characters:
 *
 *   - a pipe-table row (→ real Word table)
 *   - a markdown heading line (→ bold sub-heading paragraph)
 *   - a paragraph break (→ separate Word paragraphs)
 *   - bold/emphasis sigils (→ bold/italic runs; through insertText a
 *     single-line ``**bold**`` would insert literal asterisks)
 *   - a ~sub~ / ^sup^ marker (→ vertAlign runs; through insertText the
 *     literal ``~2~`` characters would land in the doc)
 *
 * Used by the edit-highlight Approve path to pick between the inline
 * span replace (plain prose — preserves surrounding intra-paragraph
 * formatting) and the formatter-rendered paragraph splice.
 */
// Flagless copy for .test() — RE_SUB_SUP carries `g`, whose sticky
// lastIndex would make repeated .test() calls alternate hits/misses.
const RE_SUB_SUP_PROBE = new RegExp(RE_SUB_SUP.source);

export function needsFormatterRender(text: string): boolean {
  return (
    /^\s*\|.*\|\s*$/m.test(text) || // pipe-table row
    /^#{1,6}\s/m.test(text) || // heading line
    /\n\s*\n/.test(text) || // paragraph break
    /\*\*|__/.test(text) || // bold sigils
    RE_SUB_SUP_PROBE.test(text) // ~sub~ / ^sup^ marker
  );
}

/**
 * Parse markdown content into a flat array of typed nodes.
 *
 * @param raw - The agent's markdown output for a section
 * @param sectionNumber - The parent section this content belongs to (e.g. "9.2")
 * @returns Flat array of ParsedNode for the renderer to consume
 */
export function parseMarkdown(raw: string, sectionNumber: string): ParsedNode[] {
  const allLines = raw.split("\n");
  const lines = filterContentLines(allLines, sectionNumber);

  const nodes: ParsedNode[] = [];
  let tableRows: string[][] = [];

  const flushTable = () => {
    if (tableRows.length > 0) {
      nodes.push({ type: "table", tableRows });
      tableRows = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const trimmed = rawLine.trim();

    // Blank line
    if (!trimmed) {
      flushTable();
      continue;
    }

    // Table separator row (|---|---|) — skip
    if (RE_TABLE_SEP.test(trimmed)) continue;

    // Table data row
    const tableMatch = RE_TABLE_ROW.exec(trimmed);
    if (tableMatch) {
      const cells = tableMatch[1].split("|").map((c) => c.trim());
      tableRows.push(cells);
      continue;
    }

    // Non-table line — flush any accumulated table rows
    flushTable();

    // Markdown image: ![alt](url)
    const imageMatch = RE_IMAGE.exec(trimmed);
    if (imageMatch) {
      nodes.push({
        type: "image",
        imageAlt: imageMatch[1] || "",
        imageUrl: imageMatch[2] || "",
      });
      continue;
    }

    // Agent pointer: [IMAGE DATA: caption] — resolve the caption to a real
    // image via /api/find-image and embed it (caption beneath). Reuses the
    // markdown-image node path, so insertion + caption rendering are unchanged.
    const imageDataMatch = RE_IMAGE_DATA.exec(trimmed);
    if (imageDataMatch) {
      const caption = (imageDataMatch[1] || "").trim();
      nodes.push({
        type: "image",
        imageAlt: caption,
        imageUrl: `/api/find-image?caption=${encodeURIComponent(caption)}`,
      });
      continue;
    }

    // Markdown heading (# through ######)
    const headingMatch = RE_HEADING.exec(trimmed);
    if (headingMatch) {
      // Regex capture is 1..6 hashes; cast is safe by construction.
      const level = headingMatch[1].length as 1 | 2 | 3 | 4 | 5 | 6;
      const text = headingMatch[2].replace(/\*\*/g, "").trim();
      nodes.push({
        type: "heading",
        headingLevel: level,
        headingText: text,
      });
      continue;
    }

    // Blockquote (> text)
    const blockquoteMatch = RE_BLOCKQUOTE.exec(trimmed);
    if (blockquoteMatch) {
      const depth = blockquoteMatch[1].length; // number of > chars
      const content = blockquoteMatch[2].trim();
      if (content) {
        nodes.push({
          type: "blockquote",
          segments: parseInlineSegments(content),
          indentLevel: depth - 1,
        });
      }
      continue;
    }

    // Unordered list (- item or * item)
    const ulMatch = RE_UNORDERED_LIST.exec(rawLine);
    if (ulMatch) {
      const indent = Math.floor(ulMatch[1].length / 2); // 2 spaces per level
      const content = ulMatch[2].trim();
      nodes.push({
        type: "list_item",
        listStyle: "bullet",
        segments: parseInlineSegments(content),
        indentLevel: indent,
      });
      continue;
    }

    // Ordered list (1. item)
    const olMatch = RE_ORDERED_LIST.exec(rawLine);
    if (olMatch) {
      const indent = Math.floor(olMatch[1].length / 2);
      const content = olMatch[2].trim();
      nodes.push({
        type: "list_item",
        listStyle: "numbered",
        segments: parseInlineSegments(content),
        indentLevel: indent,
      });
      continue;
    }

    // Normal paragraph — parse inline formatting
    const segments = parseInlineSegments(trimmed);
    nodes.push({ type: "paragraph", segments });
  }

  // Flush trailing table
  flushTable();

  return nodes;
}

// ---------------------------------------------------------------------------
// Table-cell image parsing
// ---------------------------------------------------------------------------

/**
 * Detect an image marker that is the ENTIRE content of a table cell, and
 * resolve it to a `{ alt, url }` the image second-pass can fetch.
 *
 * WHY this exists separately from the block-image branch in `parseMarkdown`:
 * a pipe-table row is matched (`RE_TABLE_ROW`) and consumed into a `table`
 * node BEFORE the `![alt](url)` / `[IMAGE DATA: …]` branches ever run, so an
 * image sitting inside a cell (e.g. the CMC "Photograph Comparison" table's
 * "Visual Image" row) would otherwise render as literal markdown text. The
 * table renderer calls this per cell to embed a real picture instead.
 *
 * Returns `null` when the cell is not a lone image marker (leave it to the
 * inline-segment renderer). URL policy mirrors the block-image + `[IMAGE
 * DATA:]` paths:
 *   - `[IMAGE DATA: caption]` → `/api/find-image?caption=<caption>` (the
 *     server resolves via find_image, fuzzy-matched on the caption).
 *   - `![alt](url)` with a servable url (`/api/…`, `http(s):`, `data:`) → the
 *     url verbatim.
 *   - `![alt](./images/<txn>.jpg)` and other non-servable relative paths →
 *     `/api/find-image?caption=<alt>`. The drafting model pastes back the raw
 *     path `find_image` returned — an ephemeral server-side export dir the
 *     add-in can't reach. The alt text IS the caption `find_image` was queried
 *     with, so re-resolving by caption fetches the same figure (and, because
 *     the caption carries the strength, the CORRECT one even if the model
 *     pasted a sibling row's path).
 */
export function parseCellImage(cellText: string): { alt: string; url: string } | null {
  const t = (cellText ?? "").trim();
  if (!t) return null;

  const img = RE_IMAGE.exec(t);
  if (img) {
    const alt = (img[1] || "").trim();
    const rawUrl = (img[2] || "").trim();
    const servable = /^(?:https?:|data:|\/api\/)/i.test(rawUrl);
    const url = servable || !alt ? rawUrl : `/api/find-image?caption=${encodeURIComponent(alt)}`;
    return { alt, url };
  }

  const data = RE_IMAGE_DATA.exec(t);
  if (data) {
    const caption = (data[1] || "").trim();
    return { alt: caption, url: `/api/find-image?caption=${encodeURIComponent(caption)}` };
  }

  return null;
}

// ---------------------------------------------------------------------------
// Inline segment parsing
// ---------------------------------------------------------------------------

/**
 * Parse a single line into inline segments with formatting metadata.
 *
 * Processing order (most specific patterns first):
 *   1. Split on **bold** markers
 *   2. Within non-bold spans, split on *italic* markers
 *   3. Within all spans, split on [text](url) links
 *   4. Within all spans, split on flag markers ([NOT FOUND], [REVIEWER:], [FILL:])
 *   5. Within remaining plain text, split on ~sub~ / ^sup^ markers
 *
 * OCP: adding a new inline pattern = add a new split step.
 * Existing steps unchanged.
 */
export function parseInlineSegments(line: string): InlineSegment[] {
  const segments: InlineSegment[] = [];

  // Step 1: Split on bold markers
  const boldParts = line.split(/\*\*(.+?)\*\*/g);

  for (let i = 0; i < boldParts.length; i++) {
    const part = boldParts[i];
    if (!part) continue;

    const isBold = i % 2 === 1;

    if (isBold) {
      // Bold span — check for markers inside
      const markerSegments = splitInlineMarkers(part);
      for (const seg of markerSegments) {
        if (seg.type === "text") {
          segments.push({ ...seg, type: "bold" });
        } else if (seg.type === "subscript" || seg.type === "superscript") {
          // Can't remap to "bold" without losing the vertAlign — bold
          // rides along as a flag instead (see InlineSegment.bold).
          segments.push({ ...seg, bold: true });
        } else {
          segments.push(seg);
        }
      }
    } else {
      // Non-bold span — check for italic, links, markers
      const italicAndLinkSegments = splitItalicAndLinks(part);
      segments.push(...italicAndLinkSegments);
    }
  }

  return segments;
}

/**
 * Split text on *italic* markers and [text](url) links,
 * then pass through marker splitting for each remaining text span.
 */
function splitItalicAndLinks(text: string): InlineSegment[] {
  const segments: InlineSegment[] = [];

  // Combined regex: match either *italic* or [text](url)
  const RE_INLINE = /(?<!\*)\*([^*]+?)\*(?!\*)|\[([^\]]+)\]\(([^)]+)\)/g;
  let lastEnd = 0;

  for (const match of text.matchAll(RE_INLINE)) {
    // Plain text before this match
    if ((match.index ?? 0) > lastEnd) {
      segments.push(...splitInlineMarkers(text.slice(lastEnd, match.index)));
    }

    if (match[1] !== undefined) {
      // Italic: *text*
      segments.push({ text: match[1], type: "italic" });
    } else if (match[2] !== undefined) {
      // Link: [text](url)
      segments.push({ text: match[2], type: "link", href: match[3] });
    }

    lastEnd = (match.index ?? 0) + match[0].length;
  }

  // Trailing text
  if (lastEnd < text.length) {
    segments.push(...splitInlineMarkers(text.slice(lastEnd)));
  }

  if (segments.length === 0) {
    return splitInlineMarkers(text);
  }

  return segments;
}

/**
 * Split text on inline markers: [NOT FOUND - ...], [REVIEWER: ...], [FILL: ...].
 *
 * Mirrors word_writer.py _split_inline_markers (line 311).
 */
function splitInlineMarkers(text: string): InlineSegment[] {
  const combined = new RegExp(
    "(" +
      "\\[NOT FOUND\\s*-\\s*(?:NOT IN SOURCE|RETRIEVAL FAILURE|AWAITING DOCUMENT)\\]" +
      // Accept colon, em-dash, or hyphen as the separator, plus optional
      // whitespace just inside the bracket. The agent's standard form is
      // `[MISSING INFO: <brief>]` (colon) — see src/csr_sim/flags.yaml —
      // but older drafts use `–` / `-` and may pad the brackets.
      "|\\[\\s*MISSING\\s+INFO\\s*[:\\u2013-]\\s*[^\\]]+\\]" +
      // Same separator set as MISSING INFO — colon, em-dash, or hyphen.
      "|\\[\\s*CANNOT\\s+RESOLVE\\s*[:\\u2013-]\\s*[^\\]]+\\]" +
      // [MISSING TABLE: <caption>] — required table absent from sources.
      "|\\[\\s*MISSING\\s+TABLE\\s*[:\\u2013-]\\s*[^\\]]+\\]" +
      "|\\[TO FILL\\]" +
      // [REGULATOR FLAG: <requirement>] — regulatory-audit recommendation.
      "|\\[REGULATOR FLAG:\\s*[^\\]]+\\]" +
      // [LANGUAGE: <fix>] — language check recommendation (US-English spelling).
      "|\\[LANGUAGE:\\s*[^\\]]+\\]" +
      "|\\[REVIEWER:\\s*.+?\\]" +
      "|\\[FILL:\\s*.+?\\]" +
      ")",
    "gi",
  );

  const segments: InlineSegment[] = [];
  let lastEnd = 0;

  for (const match of text.matchAll(combined)) {
    // Plain text before the marker
    if ((match.index ?? 0) > lastEnd) {
      segments.push(...splitSubSup(text.slice(lastEnd, match.index)));
    }

    const marker = match[0];

    if (marker.startsWith("[NOT FOUND")) {
      const catMatch = marker.match(/NOT IN SOURCE|RETRIEVAL FAILURE|AWAITING DOCUMENT/);
      segments.push({
        text: marker,
        type: "not_found",
        notFoundCategory: (catMatch?.[0] as NotFoundCategory) ?? "NOT IN SOURCE",
      });
    } else if (/^\[\s*MISSING\s+INFO/i.test(marker)) {
      // agent_drafter gap tag — rendered red so writers see risk areas.
      segments.push({ text: marker, type: "missing_info" });
    } else if (/^\[\s*CANNOT\s+RESOLVE/i.test(marker)) {
      // Written-off gap (retrieval cascade exhausted) — same red treatment
      // as missing_info; they're both "this is a gap" from the writer's POV.
      segments.push({ text: marker, type: "cannot_resolve" });
    } else if (/^\[\s*MISSING\s+TABLE/i.test(marker)) {
      // Required table absent from the source set — same red treatment as
      // missing_info; flags the spot where a table belongs.
      segments.push({ text: marker, type: "missing_table" });
    } else if (marker === "[TO FILL]") {
      segments.push({ text: marker, type: "fill" });
    } else if (/^\[REGULATOR FLAG:/i.test(marker)) {
      // Regulatory-audit recommendation — rendered blue (see flags.yaml).
      segments.push({ text: marker, type: "regulator_flag" });
    } else if (/^\[LANGUAGE:/i.test(marker)) {
      // Language-check recommendation — rendered green (see flags.yaml).
      segments.push({ text: marker, type: "language_flag" });
    } else if (marker.startsWith("[REVIEWER:")) {
      segments.push({ text: marker, type: "reviewer" });
    } else if (marker.startsWith("[FILL:")) {
      segments.push({ text: marker, type: "fill" });
    }

    lastEnd = (match.index ?? 0) + marker.length;
  }

  // Trailing text
  if (lastEnd < text.length) {
    segments.push(...splitSubSup(text.slice(lastEnd)));
  }

  return segments.length > 0 ? segments : splitSubSup(text);
}

/**
 * Split a plain-text span on `~sub~` / `^sup^` markers into
 * text/subscript/superscript segments. The marker characters are consumed;
 * prose tildes/carets (`~50%`, `10^9` with no closing marker) never match
 * and stay literal. Mirrors ``_emit_sub_sup_segments`` in word_writer.py.
 */
function splitSubSup(text: string): InlineSegment[] {
  if (!text.includes("~") && !text.includes("^")) {
    return text ? [{ text, type: "text" }] : [];
  }

  const segments: InlineSegment[] = [];
  let lastEnd = 0;

  for (const match of text.matchAll(RE_SUB_SUP)) {
    if ((match.index ?? 0) > lastEnd) {
      segments.push({ text: text.slice(lastEnd, match.index), type: "text" });
    }
    if (match[1] !== undefined) {
      segments.push({ text: match[1], type: "subscript" });
    } else {
      segments.push({ text: match[2], type: "superscript" });
    }
    lastEnd = (match.index ?? 0) + match[0].length;
  }

  if (lastEnd < text.length) {
    segments.push({ text: text.slice(lastEnd), type: "text" });
  }

  return segments.length > 0 ? segments : [{ text, type: "text" }];
}
